import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ColumnLayout,
  FormField,
  Header,
  Input,
  Modal,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { Page } from 'src/routes';
import { createResourceLinkForDataPermission } from 'src/api/permissions';
import {
  DATA_PERMISSION_CONSUMER_OPTION,
  DATA_PERMISSION_LAKE_FORMATION_TYPE,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import { CreateResourceLinkForDataPermissionRequest } from 'aws-sdk/clients/awsdatalakegladstonelambda';

export interface DatabaseResourceLinkProps extends CommonPageProps {
  visible: boolean;
  closeModal: any;
  database: any;
  notifyCreateResourceLinkFailure: any;
  notifyCreateResourceLinkSuccess: any;
}

export const DatabaseResourceLink = (props: DatabaseResourceLinkProps) => {
  const [loading, setLoading] = useState(false);
  const [resourceName, setResourceName] = useState(undefined);
  const [resourceRegion, setResourceRegion] = useState(undefined);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
  }, []);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      let request: CreateResourceLinkForDataPermissionRequest = {
        dataPermissionId: props.database.dataPermissionId,
        resourceLink: {
          ownerId: props.activeWorkspace.workspaceId,
          option: DATA_PERMISSION_CONSUMER_OPTION,
          type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
          dataConsumerRole: props.activeWorkspace.workspaceRoleArn,
          region: resourceRegion,
          resource: {
            glueDatabase: {
              name: resourceName,
              catalogId: props.activeWorkspace.accountId,
              targetDatabase: {
                catalogId: props.database.catalogId,
                bucketName: props.database.bucketName,
                databaseName: props.database.databaseName,
                region: props.database.region,
              },
            },
          },
        },
      };
      await createResourceLinkForDataPermission(request);
      props.notifyCreateResourceLinkSuccess('Create database resource link success');
      setLoading(false);
    } catch (err) {
      console.log('Exception creating database resource link:', err);
      props.notifyCreateResourceLinkFailure('Failed to create database resource link, ' + err.message);
      setLoading(false);
    }
    props.closeModal();
  };

  return (
    <div>
      <Modal
        visible={props.visible}
        onDismiss={props.closeModal}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='link' onClick={() => props.setRedirect(Page.HOME)}>
                Cancel
              </Button>
              <Button variant='primary' loading={loading} onClick={handleConfirm}>
                Create Database Resource Link
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={
          <Header description='You can create database resource link here' variant='h1'>
            Database resource link details
          </Header>
        }
      >
        <ColumnLayout>
          <FormField
            label={'Resource link name'}
            description={
              <>
                If you plan to access the database from Amazon Athena, then the name should be under 256 characters and
                contain only lowercase letters (a-z), numbers (0-9), and underscore (_).
                <Link
                  variant={'info'}
                  href='https://docs.aws.amazon.com/athena/latest/ug/tables-databases-columns-names.html#ate-table-database-and-column-names-allow-only-underscore-special-characters'
                  external={true}
                >
                  Athena names
                </Link>
              </>
            }
          >
            <Input
              placeholder='Enter resource link name'
              ariaRequired={true}
              onChange={(event) => setResourceName(event.detail.value)}
              value={resourceName}
            />
          </FormField>
          <FormField label={'Resource link region'} description='Enter the resource link region.'>
            <Input
              placeholder='us-east-1'
              ariaRequired={true}
              onChange={(event) => setResourceRegion(event.detail.value)}
              value={resourceRegion}
            />
          </FormField>
          <FormField label={'Data source database'} description='Enter the database name of this data source.'>
            <Input ariaRequired={true} value={props.database?.databaseName} disabled />
          </FormField>
          <FormField label={'Data source catalog ID'} description='Enter the AWS account ID of this data source.'>
            <Input ariaRequired={true} value={props.database?.catalogId} disabled />
          </FormField>
          <FormField label={'Data source region'} description='Enter the region of this data source.'>
            <Input ariaRequired={true} value={props.database?.region} disabled />
          </FormField>
        </ColumnLayout>
      </Modal>
    </div>
  );
};
